/** Settings: destinations (accounts), schedule queues, contacts, the Studio runner, session. */
import { useState } from "react";
import { Link } from "../../app/router";
import { Icon } from "../../components/Icon";
import { LegalLinks } from "../../components/LegalLinks";
import { useToast } from "../../components/toast";
import { Button, Card, Dot, EmptyState, Field, PageHeader, QueryView } from "../../components/ui";
import { UsageGauge } from "../../components/UsageGauge";
import { useApi, useCache, useMutation, useStreamStatus } from "../../data/hooks";
import { useAccounts, useContacts, useDashboard, useHealth, useQueues, useStudioSettings, useYoutubeSettings } from "../../data/queries";
import type { StudioSettings } from "../../api/types";
import { dateTime, languageName, relative } from "../../lib/format";
import { runnerHealth } from "../../lib/status";
import { NotificationSettingsCard } from "../notifications/NotificationSettingsCard";
import { PublishingSettingsCard } from "../publish/PublishingPause";
import { accountStatus } from "./AccountsScreen";
import { YourDataCard } from "./YoutubeData";

export function SettingsScreen() {
  return (
    <div className="stack-lg">
      <PageHeader title="Settings" />
      <RunnerCard />
      <StudioAutonomyCard />
      <PublishingSettingsCard />
      <NotificationSettingsCard />
      <AccountsCard />
      <QueuesCard />
      <ContactsCard />
      <YourDataCard />
      <SessionCard />
      <LegalLinks describe className="settings-legal" />
    </div>
  );
}

function RunnerCard() {
  const health = useHealth();
  const dash = useDashboard().data;
  const stream = useStreamStatus();
  const r = dash?.runner;
  return (
    <Card title="Studio runner" id="runner">
      <QueryView q={health}>{(h) => (
        <ul className="list">
          {(h.runners.length ? h.runners : [{ name: "No runner registered", last_seen_at: null }]).map((x) => {
            const rh = runnerHealth(x.last_seen_at);
            return (
              <li key={x.name} className="list-row static">
                <Dot tone={rh.tone} label={rh.label} />
                <span className="list-main"><span className="list-title">{x.name}</span><span className="list-sub">{rh.label} · last seen {relative(x.last_seen_at)}</span></span>
              </li>
            );
          })}
        </ul>
      )}</QueryView>
      {r && (
        <div className="runner-usage">
          <UsageGauge label="5h" value={r.usage?.five_hour_pct ?? null} />
          <UsageGauge label="7d" value={r.usage?.seven_day_pct ?? null} />
          <span className="muted small">{r.usage?.limited ? "Usage-limited — sessions resume when the window resets." : "Subscription usage"}{r.usage?.resets_at ? ` · resets ${dateTime(r.usage.resets_at)}` : ""}{r.max_sessions ? ` · ${r.active_sessions ?? 0}/${r.max_sessions} sessions` : ""}</span>
        </div>
      )}
      <p className="muted small">Live updates: {stream === "open" ? "connected" : stream === "stopped" ? "off" : "reconnecting…"}</p>
    </Card>
  );
}

type Autonomy = StudioSettings["autonomy"];
const AUTONOMY: { key: keyof Autonomy; legend: string; options: { value: Autonomy[keyof Autonomy]; label: string; hint: string }[] }[] = [
  { key: "tools", legend: "Tools", options: [
    { value: "auto", label: "Auto", hint: "Like Claude Code's auto mode: safe actions are approved automatically. A hard safety floor always stays on." },
    { value: "ask", label: "Ask me", hint: "Anything outside the Studio's allow-list waits for your OK." },
  ] },
  { key: "decisions", legend: "Questions", options: [
    { value: "auto", label: "Decide with safe defaults and show me at review", hint: "The Studio picks the safe option, keeps going, and lists what it decided on the review screen." },
    { value: "ask", label: "Ask me", hint: "The Studio stops and waits for your answer." },
  ] },
];

/** Autonomy (owner 2026-09-25): the owner is not the bottleneck during generation — he gates the plan, the master and publishing. */
function StudioAutonomyCard() {
  const api = useApi();
  const cache = useCache();
  const toast = useToast();
  const q = useStudioSettings();
  const save = useMutation((s: StudioSettings) => api.updateStudioSettings(s), {
    onSuccess: (s) => { cache.setData("settings/studio", s); toast.ok("Saved."); },
    onError: (e) => { cache.invalidate("settings/studio"); toast.error(e, "Couldn't save"); },
  });
  return (
    <Card title="Studio" id="studio">
      <QueryView q={q}>{(s) => (
        <div className="stack">
          <h3 className="autonomy-title">Autonomy</h3>
          {AUTONOMY.map((g) => (
            <fieldset key={g.key} className="contact-pick autonomy-group" disabled={save.pending}>
              <legend className="field-label">{g.legend}</legend>
              {g.options.map((o) => (
                <label key={o.value} className="radio-row">
                  <input type="radio" name={`autonomy-${g.key}`} value={o.value} checked={s.autonomy[g.key] === o.value}
                    onChange={() => { const next = { autonomy: { ...s.autonomy, [g.key]: o.value } }; cache.setData("settings/studio", next); void save.mutate(next); }} />
                  <span className="list-main"><span className="list-title">{o.label}</span><span className="list-sub">{o.hint}</span></span>
                </label>
              ))}
            </fieldset>
          ))}
          <p className="muted small">Either way, nothing is built until you approve the plan, and nothing is published until you approve the master.</p>
        </div>
      )}</QueryView>
    </Card>
  );
}

function AccountsCard() {
  const q = useAccounts();
  const yt = useYoutubeSettings();
  return (
    <Card title="Accounts" id="accounts" action={<Link to="/settings/accounts" className="text-link">Manage</Link>}>
      <QueryView q={q}>{(items) => {
        const needs = items.filter((a) => accountStatus(a).tone === "danger");
        return items.length ? (
          <Link to="/settings/accounts" className="list-row settings-link">
            <span className={`list-icon tone-${needs.length ? "danger" : "ok"}`}><Icon name={needs.length ? "lock" : "check"} size={20} /></span>
            <span className="list-main">
              <span className="list-title">{items.length} account{items.length === 1 ? "" : "s"}{needs.length ? ` · ${needs.length} need${needs.length === 1 ? "s" : ""} reconnecting` : " · all connected"}</span>
              <span className="list-sub">{items.map((a) => a.name).join(", ")}{yt.data && !yt.data.api_audited ? " · YouTube audit pending" : ""}</span>
            </span>
            <Icon name="chevronRight" className="list-chevron" />
          </Link>
        ) : <EmptyState icon="publish" title="No accounts yet" action={<Link to="/settings/accounts" className="btn btn-secondary btn-md">Add a YouTube account</Link>}>Connect a YouTube channel to start publishing.</EmptyState>;
      }}</QueryView>
    </Card>
  );
}

function QueuesCard() {
  const q = useQueues();
  return (
    <Card title="Schedule queues" id="queues" action={<Link to="/settings/queues" className="text-link">Manage</Link>}>
      <QueryView q={q}>{(items) => items.length ? (
        <Link to="/settings/queues" className="list-row settings-link">
          <span className="list-icon tone-neutral"><Icon name="calendar" size={20} /></span>
          <span className="list-main">
            <span className="list-title">{items.length} queue{items.length === 1 ? "" : "s"}{items.some((x) => x.paused_at || !x.enabled) ? " · some paused" : ""}</span>
            <span className="list-sub">{items.map((x) => `${x.name} (${x.slots.join(", ")})`).join(" · ")}</span>
          </span>
          <Icon name="chevronRight" className="list-chevron" />
        </Link>
      ) : <EmptyState icon="calendar" title="No queues yet" action={<Link to="/settings/queues" className="btn btn-secondary btn-md">Create a queue</Link>}>Queues are one per category × language, with daily time slots and a minimum gap.</EmptyState>}</QueryView>
    </Card>
  );
}

function ContactsCard() {
  const api = useApi();
  const toast = useToast();
  const q = useContacts();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", role: "speaker", whatsapp: "", languages: "" });
  const create = useMutation(() => api.createContact({ name: form.name.trim(), role: form.role, whatsapp: form.whatsapp.trim() || undefined, languages: form.languages.split(/[,\s]+/).filter(Boolean).map((x) => x.toLowerCase()) }), {
    invalidate: ["contacts"], onSuccess: () => { setAdding(false); setForm({ name: "", role: "speaker", whatsapp: "", languages: "" }); toast.ok("Contact saved."); }, onError: (e) => toast.error(e),
  });
  return (
    <Card title="Contacts" id="contacts" action={!adding && <Button size="sm" variant="ghost" icon="plus" onClick={() => setAdding(true)}>Add</Button>}>
      {adding && (
        <form className="stack contact-form" onSubmit={(e) => { e.preventDefault(); if (form.name.trim()) void create.mutate(); }}>
          <Field label="Name">{(id) => <input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="off" required />}</Field>
          <div className="grid-2">
            <Field label="Role">{(id) => (
              <select id={id} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="speaker">Speaker</option><option value="scholar">Scholar</option><option value="native_reviewer">Native reviewer</option><option value="other">Other</option>
              </select>
            )}</Field>
            <Field label="WhatsApp" optional hint="With country code">{(id, d) => <input id={id} aria-describedby={d} type="tel" inputMode="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+1 555 123 4567" />}</Field>
          </div>
          <Field label="Languages" optional hint="e.g. en, ur">{(id, d) => <input id={id} aria-describedby={d} value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} />}</Field>
          <div className="row"><Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button><Button type="submit" variant="primary" busy={create.pending} disabled={!form.name.trim()}>Save contact</Button></div>
        </form>
      )}
      <QueryView q={q}>{(items) => items.length ? (
        <ul className="list">
          {items.map((c) => (
            <li key={c.id} className="list-row static">
              <span className="list-icon tone-neutral"><Icon name="user" size={20} /></span>
              <span className="list-main"><span className="list-title">{c.name}</span><span className="list-sub">{[c.role?.replace(/_/g, " "), c.languages.map(languageName).join(", "), c.whatsapp].filter(Boolean).join(" · ")}</span></span>
            </li>
          ))}
        </ul>
      ) : !adding && <EmptyState icon="user" title="No contacts yet">Save speakers, scholars and native reviewers to share reviews over WhatsApp in seconds.</EmptyState>}</QueryView>
    </Card>
  );
}

function SessionCard() {
  const api = useApi();
  const cache = useCache();
  const signOut = useMutation(() => api.logout(), { onSuccess: () => { cache.clear(); window.location.assign("/"); } });
  return (
    <Card title="This device">
      <p className="muted small">ContentOps {api.mode === "mock" ? "(mock data)" : ""} · build {__APP_VERSION__}</p>
      <div className="row">
        <Button variant="secondary" icon="refresh" onClick={() => window.location.reload()}>Reload app</Button>
        <Button variant="ghost" busy={signOut.pending} onClick={() => void signOut.mutate()}>Sign out</Button>
      </div>
    </Card>
  );
}
